'use client'

import SuperSelectField from '@hashicorp/react-mds/src/hds/components/form/super-select/field'
import type {
	SuperSelectOption,
	SuperSelectOptionProps,
} from '@hashicorp/react-mds/src/hds/components/form/super-select'
import { useFormContext, Controller } from 'react-hook-form'
import { useEffect, useState } from 'react'
import type { LocationResourceItem, LocationQuestions } from 'lib/careers/types'
import { v4 as uuid } from 'uuid'
import sendPosthogEvent from '@web/utils/send-posthog-event'

const PLACES_API_KEY = process.env.NEXT_PUBLIC_PLACES_API_KEY

/**
 * Formats an array of Places Autocomplete API
 * objects to options compatible with
 * `<SuperSelectField />`.
 *
 * These fields from Places Autocomplete API
 * prediction objects are mapped:
 * - `placeId`: Used for the option ID and value.
 * The latter is crucial to ensure selection logic
 * sets the right state for the component.
 * - `text.text`: Used for the option label, and
 * eventually set as the location string passed
 * to the form's data.
 */
const fmtToOptions = (items: LocationResourceItem[]): SuperSelectOption[] => {
	return items?.length >= 1
		? items.map((item) => ({
				id: item.placePrediction.placeId,
				label: item.placePrediction.text.text,
				value: item.placePrediction.placeId,
		  }))
		: []
}

const loadingOption: SuperSelectOptionProps = {
	id: 'loading',
	label: 'Loading...',
	value: 'loading',
	isLoading: true,
}

export interface LocationDropdownProps {
	question: LocationQuestions[2]
}

export function LocationDropdown({ question }: LocationDropdownProps) {
	const [filteredItems, setFilteredItems] = useState<LocationResourceItem[]>([])
	const [isLoading, setIsLoading] = useState<boolean>(false)
	const [searchTerm, setSearchTerm] = useState<string>()
	const [locationString, setLocationString] = useState<string>()
	const [placeId, setPlaceId] = useState<string>(null)
	const [sessionId, setSessionId] = useState<string>()

	const { control, formState, setValue } = useFormContext()
	const errorMsg =
		formState.errors[question.fields[0].name]?.message?.toString()

	useEffect(() => {
		// Generate new session ID for the initial autocomplete session
		setSessionId(uuid())
	}, [])

	/**
	 * Fetches a set of predictions from the Places
	 * Autocomplete API, storing them in component state.
	 */
	const fetchPredictions = async () => {
		setIsLoading(true)
		try {
			const res = await fetch(
				`https://places.googleapis.com/v1/places:autocomplete`,
				{
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						'X-Goog-Api-Key': PLACES_API_KEY,
					},
					body: JSON.stringify({
						input: searchTerm,
						// Restricts results to cities, states/provinces, and/or countries
						includedPrimaryTypes: '(cities)',
					}),
				}
			)

			const data = await res.json()
			setFilteredItems(data.suggestions)

			sendPosthogEvent('Careers location autocomplete requested', {
				input: searchTerm,
				sessionId,
			})
		} catch (error) {
			console.error(`Error fetching location autocomplete predictions:`, error)
		} finally {
			setIsLoading(false)
		}
	}

	/**
	 * Uses the given place ID to fetch latitude and longitude
	 * data for the corresponding place in the Places API.
	 *
	 * @param {String} id A Places API place ID
	 */
	const fetchLatLong = async (id: string) => {
		try {
			const res = await fetch(`https://places.googleapis.com/v1/places/${id}`, {
				method: 'GET',
				headers: {
					'Content-Type': 'application/json',
					'X-Goog-Api-Key': PLACES_API_KEY,
					'X-Goog-FieldMask': 'location',
				},
			})

			const data = await res.json()

			// Set these fields in the form data to the fetched data
			setValue('location', locationString)
			setValue('latitude', data.location.latitude)
			setValue('longitude', data.location.longitude)
			sendPosthogEvent('Careers location selected', {
				locationString,
				sessionId,
			})

			// Generate new session ID for the next autocomplete session
			setSessionId(uuid())
		} catch (error) {
			console.error(`Error fetching location details:`, error)
		}
	}

	/**
	 * Debounce prediction and lat/long data fetches
	 * on search term or place ID change
	 */
	const DEBOUNCE_TIME = 600
	useEffect(() => {
		const handler = setTimeout(() => {
			if (searchTerm) {
				fetchPredictions()
			}
		}, DEBOUNCE_TIME)

		return () => clearTimeout(handler)
	}, [searchTerm])

	useEffect(() => {
		const handler = setTimeout(() => {
			if (placeId) {
				fetchLatLong(placeId)
			}
		}, DEBOUNCE_TIME)

		return () => clearTimeout(handler)
	}, [placeId])

	const options = isLoading ? [loadingOption] : fmtToOptions(filteredItems)
	return (
		<Controller
			control={control}
			name={question.fields[0].name}
			defaultValue={''}
			render={({ field }) => (
				<SuperSelectField
					label="Location (City)"
					error={errorMsg}
					isRequired={question.required}
					listLabel="Location (City)"
					options={options}
					onChangeSelectedOption={(option) => {
						setPlaceId(option.value)
						setLocationString(option.label)
						field.onChange(option.value)
					}}
					onSearchBlur={field.onBlur}
					searchProps={{
						placeholder: `Search for a location`,
						onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
							setSearchTerm(e.target.value),
					}}
				/>
			)}
		/>
	)
}
